/**
 * 공용 UI 프리미티브.
 *
 * 투자자 데모 품질을 위해 typography·여백·shadow 규약을 중앙화.
 */
import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react'

type Variant = 'primary' | 'accent' | 'ghost' | 'outline' | 'danger'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  loading?: boolean
  icon?: ReactNode
}

const variantClass: Record<Variant, string> = {
  primary: 'btn-primary',
  accent: 'btn-accent',
  ghost: 'btn-ghost',
  outline: 'btn-outline',
  danger: 'btn bg-red-600 text-white shadow-soft hover:bg-red-500 active:scale-[0.98]',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', loading, icon, children, className = '', disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`${variantClass[variant]} ${className}`}
      {...rest}
    >
      {loading ? <Spinner size={14} /> : icon}
      {children}
    </button>
  )
})

export function Card({
  children,
  className = '',
  interactive,
}: {
  children: ReactNode
  className?: string
  interactive?: boolean
}) {
  return (
    <div
      className={`surface ${interactive ? 'cursor-pointer transition-colors hover:border-slate-300 hover:bg-slate-50/50' : ''} ${className}`}
    >
      {children}
    </div>
  )
}

export function CardHeader({
  title,
  subtitle,
  action,
  icon,
}: {
  title: ReactNode
  subtitle?: ReactNode
  action?: ReactNode
  icon?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
      <div className="flex min-w-0 items-start gap-3">
        {icon && (
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600">
            {icon}
          </div>
        )}
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  )
}

export function CardBody({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`p-5 ${className}`}>{children}</div>
}

type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'brand'

const badgeTone: Record<BadgeTone, string> = {
  neutral: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200',
  info: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200',
  success: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
  warning: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
  danger: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  brand: 'bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200',
}

export function Badge({
  tone = 'neutral',
  children,
  icon,
  className = '',
}: {
  tone?: BadgeTone
  children: ReactNode
  icon?: ReactNode
  className?: string
}) {
  return (
    <span className={`chip ${badgeTone[tone]} ${className}`}>
      {icon}
      {children}
    </span>
  )
}

export function StatusDot({ tone }: { tone: BadgeTone }) {
  const dot: Record<BadgeTone, string> = {
    neutral: 'bg-slate-400',
    info: 'bg-sky-500',
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    danger: 'bg-red-500',
    brand: 'bg-brand-500',
  }
  return <span className={`h-1.5 w-1.5 rounded-full ${dot[tone]}`} />
}

export function Stat({
  label,
  value,
  hint,
  tone = 'brand',
  icon,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  tone?: BadgeTone
  icon?: ReactNode
}) {
  const accent: Record<BadgeTone, string> = {
    neutral: 'border-l-slate-400',
    info: 'border-l-sky-500',
    success: 'border-l-emerald-500',
    warning: 'border-l-amber-500',
    danger: 'border-l-red-500',
    brand: 'border-l-indigo-500',
  }
  return (
    <div className={`surface relative overflow-hidden border-l-2 ${accent[tone]}`}>
      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-500">{label}</span>
          {icon && <span className="text-slate-400">{icon}</span>}
        </div>
        <div className="mt-2 text-2xl font-semibold text-slate-900 sm:text-3xl">{value}</div>
        {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
      </div>
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  hint?: string
  error?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, className = '', ...rest },
  ref,
) {
  return (
    <div className={className}>
      {label && <span className="label">{label}</span>}
      <input ref={ref} className={`input ${error ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20' : ''}`} {...rest} />
      {hint && !error && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
})

export function EmptyState({
  title,
  description,
  icon,
  action,
  className = '',
}: {
  title: string
  description?: string
  icon?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div className={`surface flex flex-col items-center justify-center px-8 py-12 text-center ${className}`}>
      {icon && (
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-slate-100 text-slate-600">
          {icon}
        </div>
      )}
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

export function Spinner({ size = 16, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={`animate-spin ${className}`}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`skeleton rounded-lg ${className}`} />
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {eyebrow && (
          <span className="text-[11px] font-semibold uppercase text-indigo-600">
            {eyebrow}
          </span>
        )}
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {action && <div className="w-full shrink-0 sm:w-auto">{action}</div>}
    </div>
  )
}

export function Divider() {
  return <div className="h-px w-full bg-slate-200" />
}

export function HelpText({ children }: { children: ReactNode }) {
  return <p className="text-xs text-slate-500">{children}</p>
}

export function KeyValue({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900">{value}</span>
    </div>
  )
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ id: T; label: string; count?: number | string; icon?: ReactNode }>
  active: T
  onChange: (id: T) => void
}) {
  return (
    <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={`flex min-h-8 flex-1 items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            active === t.id
              ? 'bg-slate-900 text-white shadow-soft'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          {t.icon}
          {t.label}
          {t.count !== undefined && (
            <span
              className={`rounded-full px-1.5 text-xs ${
                active === t.id ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'
              }`}
            >
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

export function FloatingBackground({ className = '' }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none fixed inset-0 -z-10 bg-slate-50 ${className}`}
    />
  )
}

export function IconWrap({
  children,
  tone = 'brand',
  className = '',
}: {
  children: ReactNode
  tone?: BadgeTone
  className?: string
}) {
  const bg: Record<BadgeTone, string> = {
    neutral: 'bg-slate-100 text-slate-700',
    info: 'bg-sky-50 text-sky-600',
    success: 'bg-emerald-50 text-emerald-600',
    warning: 'bg-amber-50 text-amber-600',
    danger: 'bg-red-50 text-red-600',
    brand: 'bg-brand-50 text-brand-600',
  }
  return (
    <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${bg[tone]} ${className}`}>
      {children}
    </span>
  )
}

export function HButton({
  children,
  ...rest
}: HTMLAttributes<HTMLButtonElement> & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      {...rest}
    >
      {children}
    </button>
  )
}
